import React from 'react';

import { SchemaHolder } from './schema';
import { QueryParameters, ChangeSource, QueryParametersKey } from './types';

import { useFilterField } from './useFilterField';
import { useFilterSummary } from './useFilterSummary';
import { FilterSummary as BaseFilterSummary } from './FilterSummary';
import { QueryParameterContextProvider, QueryParameterContextState, useContext } from './context';

export function createQueryParameterContext<StaticProps, SearchQuery, Parameters extends QueryParameters>(
  schema: SchemaHolder<StaticProps, SearchQuery, Parameters>
) {
  const staticPropsContext = React.createContext<StaticProps | undefined>(undefined);
  const parametersContext = React.createContext<QueryParameterContextState<Parameters> | undefined>(undefined);

  function Provider(props: { children: React.ReactNode; staticProps: StaticProps; defaultParameters: Parameters }) {
    return (
      <staticPropsContext.Provider value={props.staticProps}>
        {QueryParameterContextProvider<StaticProps, SearchQuery, Parameters>({
          context: parametersContext,
          schemaHolder: schema,
          staticProps: props.staticProps,
          defaultParameters: props.defaultParameters,
          children: props.children,
        })}
      </staticPropsContext.Provider>
    );
  }
  function useQueryParameter() {
    const value = React.useContext(parametersContext);
    if (value === undefined) {
      throw new Error('useQueryParameterContext must be used within a QueryParameterContextProvider');
    }
    return value;
  }
  function useStaticProps() {
    const value = React.useContext(staticPropsContext);
    if (value === undefined) {
      throw new Error('useStaticProps must be used within a staticPropsContextProvider');
    }
    return value;
  }
  function useInternalContext() {
    return useContext(parametersContext);
  }
  function useInternalFilterField<FilterKey extends QueryParametersKey<Parameters>>(
    filterKey: FilterKey,
    source: ChangeSource
  ) {
    const staticProps = useStaticProps();
    const ctx = useInternalContext();
    return useFilterField<StaticProps, SearchQuery, Parameters, FilterKey>({
      source,
      filterKey,
      staticProps,
      schemaHolder: schema,
      context: parametersContext,
      defaultParameters: ctx.parameters,
    });
  }
  function useInternalFilterSummary() {
    const staticProps = useStaticProps();
    const ctx = useInternalContext();
    return useFilterSummary<StaticProps, SearchQuery, Parameters>({
      staticProps,
      parameters: ctx.parameters,
      defaultParameters: ctx.parameters,
      schema: schema,
    });
  }
  function FilterSummary() {
    const state = useInternalContext();
    return BaseFilterSummary<Parameters>(state);
  }

  return {
    Provider,
    schema,
    FilterSummary,
    useQueryParameter,
    useContext: useInternalContext,
    useFilterField: useInternalFilterField,
    useFilterSummary: useInternalFilterSummary,
  };
}

export type Context<StaticProps, SearchQuery, Parameters extends QueryParameters> = ReturnType<
  typeof createQueryParameterContext<StaticProps, SearchQuery, Parameters>
>;
